import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { UrlConnectionEnum } from '../../enums/url-connection.enum';
import { HttpService } from '../http-service/http.service';
import { SettingsService } from '../setting-service/setting.service';

export interface EmailTemplate {
  id: number;
  nome: string;
  assunto_padrao: string;
  /** Status de pedido ao qual o template está associado (`null` = uso livre). */
  status_associado: number | null;
  ativo: boolean;
  /**
   * Corpo do e-mail, usado **apenas** para pré-visualizar antes do envio. O
   * disparo continua sendo feito pelo `id` — o painel nunca devolve este HTML
   * para a API.
   */
  html?: string;
}

export interface DispatchRecipient {
  nome: string;
  email: string;
}

export interface DispatchFailure {
  email: string;
  motivo: string;
}

export interface MassActionResult extends DispatchResult {
  atualizados: number;
  sem_alteracao: number;
  template: string | null;
}

export interface DispatchResult {
  id_disparo: number;
  enviados: number;
  falhas: DispatchFailure[];
}

/** Uma ação registrada no histórico (a camada do lote). */
export interface HistoryAction {
  id_acao: number;
  itens?: HistoryItem[];
  tipo: 'processamento' | 'disparo' | 'PROCESSAMENTO MASSIVO' | 'DISPARO PONTUAL';
  data: string;
  administrador: { nome: string; email: string };
  acao: string;
  quantidade: number;
  template: string | null;
  sucessos: number;
  falhas: number;
  /**
   * Nome dado ao disparo pontual no momento do envio, para localizá-lo depois.
   * Nulo em processamento massivo, que é identificado pelo status aplicado.
   */
  nome?: string | null;
  /** Assunto do e-mail enviado, exibido como contexto do nome. */
  assunto?: string | null;
}

/** Um registro afetado por uma ação (a camada de detalhe). */
export interface HistoryItem {
  estado?: string;
  numero_pedido: string;
  nome_medico: string;
  status_anterior: string;
  status_novo: string;
  email_enviado: boolean;
  motivo_falha: string | null;
}

/** Vincula dados carregados à sessão e ao destino que os originaram. */
export function captureApiContext(settings: SettingsService): () => boolean {
  const token = settings.user?.token;
  const url = settings.configUso?.urlApiPadrao;
  return () =>
    !!token && !!url && token === settings.user?.token && url === settings.configUso?.urlApiPadrao;
}

@Injectable({ providedIn: 'root' })
export class CommunicationService {
  constructor(
    private http: HttpService,
    private settings: SettingsService,
  ) {}
  captureContext(): () => boolean {
    return captureApiContext(this.settings);
  }
  get modoLocal(): boolean {
    return false;
  }
  private async request<T>(
    method: 'get' | 'post',
    endpoint: string,
    body: object = {},
  ): Promise<T> {
    const token = this.settings.user?.token,
      api = this.settings.configUso.urlApiPadrao || '';
    if (
      !token ||
      !(
        api.startsWith('https://api.pdag.chiesi.com.br/') ||
        api.startsWith('https://api.dev.pdag.chiesi.com.br/') ||
        api.startsWith('http://localhost:3014/')
      )
    )
      throw Error('Entre novamente para acessar as comunicações.');
    const params =
      method === 'get'
        ? Object.fromEntries(
            Object.entries(body).filter(
              ([, value]) => value !== undefined && value !== null && value !== '',
            ),
          )
        : body;
    const response = await lastValueFrom(
      this.http[method](endpoint, params, this.http.setHeaderConfig(true)),
    );
    if (this.settings.user?.token !== token || this.settings.configUso.urlApiPadrao !== api)
      throw Error('A sessão mudou. Entre novamente.');
    if (response.status !== 200 || response.result == null)
      throw Error(response.message || 'Não foi possível concluir a operação.');
    return response.result as T;
  }
  getTemplates(): Promise<EmailTemplate[]> {
    return this.request('get', UrlConnectionEnum.GET_EMAIL_TEMPLATES);
  }
  createTemplate(payload: {
    nome: string;
    assunto_padrao: string;
    html: string;
  }): Promise<EmailTemplate> {
    return this.request('post', UrlConnectionEnum.CREATE_EMAIL_TEMPLATE, payload);
  }
  sendDispatch(payload: {
    nome: string;
    assunto: string;
    template_id?: number | null;
    html?: string | null;
    destinatarios: DispatchRecipient[];
    disparo: string;
    chave_idempotencia?: string;
  }): Promise<DispatchResult> {
    if (payload.disparo !== 'pontual')
      return Promise.reject(
        Error('Use a operação integrada de status e comunicação para ações em massa.'),
      );
    return this.request('post', UrlConnectionEnum.SEND_DISPATCH_OCCASIONAL, {
      ...payload,
      chave_idempotencia: payload.chave_idempotencia || crypto.randomUUID(),
    });
  }
  processOrders(payload: {
    status: number;
    resgates: number[];
    template_id: number | null;
    comunicado_por_email: boolean;
    chave_idempotencia: string;
  }): Promise<MassActionResult> {
    return this.request('post', UrlConnectionEnum.SEND_DISPATCH_MASSIVE, payload);
  }
  getHistory(filters: object = {}): Promise<HistoryAction[]> {
    return this.request('get', UrlConnectionEnum.GET_COMMUNICATION_HISTORY, filters);
  }
  getHistoryDetails(idAcao: number): Promise<HistoryItem[]> {
    return this.request('get', UrlConnectionEnum.GET_COMMUNICATION_HISTORY_DETAILS, {
      id_acao: idAcao,
    });
  }
  async getCommunicationsHistory(filters: object = {}) {
    return { status: 200, result: await this.getHistory(filters) };
  }
}
